import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { statusLook } from "@/components/emc/status";
import type { StatusDomain, Tone } from "@/components/emc/status";
import { TONE_SOFT } from "@/components/emc/tone";

// The pill marks a state and only a state — never a label, a country or a
// category. A shadcn Badge is deliberately not in the kit. It is the one
// capsule in the product: a soft fill, the darker ink of the same hue (4.5:1),
// and a dot, so the state does not rest on hue alone.

type Props =
  | {
      /** Which state machine the value belongs to (see status.ts). */
      domain: StatusDomain;
      state: string;
      tone?: never;
      children?: ReactNode;
      title?: string;
      className?: string;
    }
  | {
      /** A tone computed by the page from a rule it owns (a threshold). */
      tone: Tone;
      domain?: never;
      state?: never;
      children: ReactNode;
      title?: string;
      className?: string;
    };

export function StatusPill(props: Props) {
  const look =
    props.domain !== undefined
      ? statusLook(props.domain, props.state)
      : { tone: props.tone, label: null };
  const title = props.title ?? (props.domain !== undefined ? props.state : undefined);
  return (
    <span
      data-slot="status-pill"
      data-tone={look.tone}
      title={title}
      className={cn(
        "inline-flex h-5.5 max-w-full items-center gap-1.5 rounded-pill pr-2.25 pl-2 text-label font-semibold whitespace-nowrap",
        TONE_SOFT[look.tone],
        props.className,
      )}
    >
      <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
      <span className="truncate">{props.children ?? look.label}</span>
    </span>
  );
}
